import { memo, useMemo } from "react";
import styled from "styled-components";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { Characteristic } from "@sablier/v2-components/molecules";
import { useChainExplorer } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import type { IExpected } from "@sablier/v2-types";
import type { ComponentProps } from "react";

type Description = ComponentProps<typeof Characteristic>["description"];

const Wrapper = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    align-items: flex-start;
    padding-top: calc(${(props) => props.theme.sizes.edge} * 1);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
    overflow-x: hidden;
    &:not(:last-child) {
      border-bottom: 2px solid ${(props) => props.theme.colors.border};
    }
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    row-gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
  }
`;

function Item(props: IExpected) {
  const { t } = useT();
  const explorer = useChainExplorer({
    chainId: props.chainId,
    type: "tx",
    hash: props.hash,
  });

  const description = useMemo(
    (): Description => [
      {
        purpose: "label",
        options: {
          value: `${_.toDuration(props.createdOn, "date-only")[0]} (${
            props.status
          }):`,
          icon: ChevronDoubleRightIcon,
        },
      },
      {
        purpose: "content",
        options: {
          value: _.truncate(_.upperFirst(props.stream.name), { length: 60 }),
          tooltip: _.upperFirst(props.stream.name),
        },
      },
      {
        purpose: "content",
        options: {
          value: "-",
        },
      },
      {
        purpose: "external",
        options: {
          value: t("structs.viewTx"),
          to: explorer,
        },
      },
    ],
    [explorer, props, t],
  );

  return (
    <Wrapper>
      <Content>
        <Characteristic description={description} />
      </Content>
    </Wrapper>
  );
}

export default memo(Item);
